import { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users, ShieldCheck, ShieldAlert, Archive, Ticket, UserCheck,
  Clock, TrendingDown, Loader2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Aggregate stats card shown at the top of the وكلاء الشركة tab.
 * Reads GET /api/admin/agents/analytics and renders 6 KPI tiles.
 * Silent on failure — the tab still shows the agent list.
 */
export default function AgentAnalyticsBanner({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/agents/analytics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (alive) setData(res.data);
      } catch {
        // silent
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return (
      <div className="mb-6 p-4 bg-[#0a0a0a] border border-[#2a2a4e] rounded-2xl flex items-center justify-center text-[#808080]">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  if (!data) return null;

  const { totals, by_type, invites, block_rate_percent } = data;

  const tiles = [
    {
      label: 'إجمالي الوكلاء',
      value: totals.live,
      icon: Users,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10',
      border: 'border-cyan-500/30',
    },
    {
      label: 'نشطون',
      value: totals.active,
      icon: ShieldCheck,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/30',
    },
    {
      label: 'موقوفون',
      value: totals.blocked,
      icon: ShieldAlert,
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      border: 'border-red-500/30',
    },
    {
      label: 'بدعوة',
      value: by_type.invited,
      icon: Ticket,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
    },
    {
      label: 'بتقديم طلب',
      value: by_type.applied,
      icon: UserCheck,
      color: 'text-purple-400',
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
    },
    {
      label: 'مؤرشفون',
      value: totals.archived,
      icon: Archive,
      color: 'text-slate-400',
      bg: 'bg-slate-500/10',
      border: 'border-slate-500/30',
    },
  ];

  const formatHours = (h) => {
    if (h === null || h === undefined) return '—';
    if (h < 1) return `${Math.round(h * 60)} د`;
    if (h < 48) return `${h.toFixed(1)} س`;
    return `${(h / 24).toFixed(1)} ي`;
  };

  return (
    <div className="mb-6 space-y-3" data-testid="agent-analytics-banner">
      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {tiles.map((t) => {
          const Icon = t.icon;
          return (
            <div
              key={t.label}
              data-testid={`kpi-${t.label}`}
              className={`p-3 rounded-xl border ${t.bg} ${t.border}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`w-4 h-4 ${t.color}`} />
                <span className="text-xs text-[#a0a0b0]">{t.label}</span>
              </div>
              <div className={`text-2xl font-bold ${t.color}`}>{t.value}</div>
            </div>
          );
        })}
      </div>

      {/* Secondary metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="p-3 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="text-xs text-[#a0a0b0]">متوسط زمن التفعيل</span>
          </div>
          <div className="text-lg font-bold text-white">{formatHours(invites.avg_invite_to_redeem_hours)}</div>
          <div className="text-[10px] text-[#606060] mt-0.5">
            بناءً على {invites.redeemed_count} دعوة مفعّلة
          </div>
        </div>
        <div className="p-3 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl">
          <div className="flex items-center gap-2 mb-1">
            <TrendingDown className="w-4 h-4 text-rose-400" />
            <span className="text-xs text-[#a0a0b0]">معدل الحظر</span>
          </div>
          <div className="text-lg font-bold text-white">{block_rate_percent}%</div>
          <div className="text-[10px] text-[#606060] mt-0.5">
            من إجمالي الوكلاء النشطين
          </div>
        </div>
        <div className="p-3 bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl">
          <div className="flex items-center gap-2 mb-1">
            <Ticket className="w-4 h-4 text-blue-400" />
            <span className="text-xs text-[#a0a0b0]">نسبة الوكلاء بالدعوة</span>
          </div>
          <div className="text-lg font-bold text-white">
            {totals.live ? Math.round((by_type.invited / totals.live) * 100) : 0}%
          </div>
          <div className="text-[10px] text-[#606060] mt-0.5">
            {by_type.invited} من {totals.live}
          </div>
        </div>
      </div>
    </div>
  );
}
